import { z } from "zod";

const optionalTrimmed = () => z.string().trim().optional();

/**
 * All "current" fields are read-only display values (pre-populated from
 * tempData or the authenticated employer profile); all "new" fields are
 * individually optional, but at least one must be provided and differ from
 * its current value. House-number changes are folded into address — there
 * is no dedicated field.
 */
export const EmployerUpdateRequestSchema = z
  .object({
    currentRcNumber: optionalTrimmed(),
    newRcNumber: optionalTrimmed(),
    currentName: optionalTrimmed(),
    newName: optionalTrimmed(),
    currentAddress: optionalTrimmed(),
    newAddress: optionalTrimmed(),
    currentEmail: optionalTrimmed(),
    newEmail: optionalTrimmed(),
    currentContactPersonEmail: optionalTrimmed(),
    newContactPersonEmail: optionalTrimmed(),
    currentContactPersonPhone: optionalTrimmed(),
    newContactPersonPhone: optionalTrimmed(),
    currentContactPersonRole: optionalTrimmed(),
    newContactPersonRole: optionalTrimmed(),
    comments: z.string().trim().optional(),
    contactEmail: z.email("Enter a valid contact email address."),
    documentUrl: z.string().min(1, "A supporting document is required."),
  })
  .superRefine((data, ctx) => {
    const pairs = [
      { field: "newRcNumber" as const, current: data.currentRcNumber, next: data.newRcNumber },
      { field: "newName" as const, current: data.currentName, next: data.newName },
      { field: "newAddress" as const, current: data.currentAddress, next: data.newAddress },
      { field: "newEmail" as const, current: data.currentEmail, next: data.newEmail },
      {
        field: "newContactPersonEmail" as const,
        current: data.currentContactPersonEmail,
        next: data.newContactPersonEmail,
      },
      {
        field: "newContactPersonPhone" as const,
        current: data.currentContactPersonPhone,
        next: data.newContactPersonPhone,
      },
      {
        field: "newContactPersonRole" as const,
        current: data.currentContactPersonRole,
        next: data.newContactPersonRole,
      },
    ];

    const provided = pairs.filter((pair) => !!pair.next?.trim());

    if (provided.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Enter at least one new value to request an update.",
        path: [],
      });
      return;
    }

    for (const pair of provided) {
      if (
        pair.current &&
        pair.next &&
        pair.current.trim().toLowerCase() === pair.next.trim().toLowerCase()
      ) {
        ctx.addIssue({
          code: "custom",
          message: "The new value must be different from the current value.",
          path: [pair.field],
        });
      }
    }

    if (data.newEmail && !z.email().safeParse(data.newEmail).success) {
      ctx.addIssue({
        code: "custom",
        message: "Enter a valid new email address.",
        path: ["newEmail"],
      });
    }

    if (
      data.newContactPersonEmail &&
      !z.email().safeParse(data.newContactPersonEmail).success
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Enter a valid new contact person email address.",
        path: ["newContactPersonEmail"],
      });
    }
  });
